import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'next-themes';
import { Toaster } from './components/ui/sonner';
import Lenis from 'lenis';
import '@/App.css';

import Home from './pages/Home';
import Services from './pages/Services';
import BookLesson from './pages/BookLesson';
import SwimTeam from './pages/SwimTeam';
import SchoolPartnerships from './pages/SchoolPartnerships';
import AdminDashboard from './pages/AdminDashboard';
import AdminDashboardEnhanced from './pages/AdminDashboardEnhanced';
import CoachDashboard from './pages/CoachDashboard';
import MemberPortal from './pages/MemberPortal';
import Login from './pages/Login';
import AboutUs from './pages/AboutUs';
import Contact from './pages/Contact';
import FAQs from './pages/FAQs';
import SchedulesPricing from './pages/SchedulesPricing';
import Policies from './pages/Policies';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Initialize Lenis smooth scroll
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      smoothWheel: true,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    // Check for stored token
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);

    return () => lenis.destroy();
  }, []);

  const handleLogin = (userData, token) => {
    setUser(userData);
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-xl text-slate-600">Loading...</div>
    </div>;
  }

  return (
    // Same storage key and class as the pre-paint script in public/index.html.
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem storageKey="theme" disableTransitionOnChange>
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home user={user} onLogout={handleLogout} />} />
          <Route path="/services" element={<Services user={user} onLogout={handleLogout} />} />
          <Route path="/about" element={<AboutUs user={user} onLogout={handleLogout} />} />
          <Route path="/contact" element={<Contact user={user} onLogout={handleLogout} />} />
          <Route path="/faqs" element={<FAQs user={user} onLogout={handleLogout} />} />
          <Route path="/schedules-pricing" element={<SchedulesPricing user={user} onLogout={handleLogout} />} />
          <Route path="/policies" element={<Policies user={user} onLogout={handleLogout} />} />
          <Route path="/book-lesson" element={<BookLesson user={user} onLogout={handleLogout} />} />
          <Route path="/swim-team" element={<SwimTeam user={user} onLogout={handleLogout} />} />
          <Route path="/partnerships" element={<SchoolPartnerships user={user} onLogout={handleLogout} />} />
          <Route path="/login" element={<Login onLogin={handleLogin} />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/admin"
            element={
              user?.role === 'admin' ? (
                <AdminDashboardEnhanced user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/coach"
            element={
              user?.role === 'coach' || user?.role === 'admin' ? (
                <CoachDashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/member-portal"
            element={
              user ? (
                <MemberPortal user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
    </ThemeProvider>
  );
}

export default App;
